import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "store/hooks";
import { handleAddDriveOrderRequest } from "store/modules/Orders/thunk.store";
import { getDealerCoverageAreaDetail } from "services/logistics/getDealerCoverageAreas";
import { getDealerDrivers } from "services/logistics/getDealerDrivers";
import { getDealerDriversInsight } from "services/order/getDealerDriversInsight";
import { CoverageAreaOrderDetailDto } from "types/coverageAreaOrderDetail";
import { DealerDriverDto, isOnlineDriverStatus } from "types/dealerDrivers";

import {
  buildDriverSelectOptions,
  collectListedDriverIds,
  DriverSelectOption,
  NO_DRIVER_OPTION_ID,
} from "../OrderDriverAutocomplete/buildDriverSelectOptions";

interface UseOrderDriverSelectionParams {
  dealerId: string;
  orderId: string;
  coverageAreaId?: string;
}

export function useOrderDriverSelection({
  dealerId,
  orderId,
  coverageAreaId,
}: UseOrderDriverSelectionParams) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [dealerDrivers, setDealerDrivers] = useState<DealerDriverDto[]>();
  const [coverageAreaDetail, setCoverageAreaDetail] =
    useState<CoverageAreaOrderDetailDto | null>(null);
  const [driverActiveOrdersById, setDriverActiveOrdersById] = useState<
    Map<string, number> | undefined
  >();
  const [driverInsightLoaded, setDriverInsightLoaded] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [selectedDriver, setSelectedDriver] =
    useState<DriverSelectOption | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const driverOptions = useMemo(
    () =>
      buildDriverSelectOptions(
        coverageAreaDetail,
        dealerDrivers,
        {
          noDriver: t("noDeliveryDriver"),
          available: t("driverDropdownStatusAvailable"),
          onDelivery: t("driverDropdownStatusOnDelivery"),
        },
        driverActiveOrdersById,
      ),
    [coverageAreaDetail, dealerDrivers, driverActiveOrdersById, t],
  );

  const fetchDealerDrivers = () =>
    getDealerDrivers(dealerId, { statuses: ["online"] });

  const fetchCoverageAreaDetail = async () => {
    if (!dealerId || !coverageAreaId) {
      return null;
    }
    const coverageArea = await getDealerCoverageAreaDetail(
      dealerId,
      coverageAreaId,
    );
    return {
      id: coverageArea.id,
      type: coverageArea.type,
      drivers: coverageArea.drivers,
    } as CoverageAreaOrderDetailDto;
  };

  const fetchDriverInsights = async (
    coverageDetail: CoverageAreaOrderDetailDto | null,
    drivers: DealerDriverDto[] | undefined,
  ) => {
    const driverIds = collectListedDriverIds(coverageDetail, drivers);
    if (!dealerId || driverIds.length === 0) {
      setDriverActiveOrdersById(undefined);
      setDriverInsightLoaded(false);
      return;
    }

    try {
      const activeOrdersById = await getDealerDriversInsight({
        dealerId,
        driverIds,
      });
      setDriverActiveOrdersById(activeOrdersById);
      setDriverInsightLoaded(true);
    } catch {
      setDriverActiveOrdersById(undefined);
      setDriverInsightLoaded(false);
    }
  };

  const loadSelectionData = async () => {
    setLoadingDrivers(true);
    setDealerDrivers(undefined);
    setCoverageAreaDetail(null);
    setDriverActiveOrdersById(undefined);
    setDriverInsightLoaded(false);

    const [driversResult, coverageResult] = await Promise.allSettled([
      fetchDealerDrivers(),
      fetchCoverageAreaDetail(),
    ]);

    let drivers: DealerDriverDto[] | undefined;
    if (driversResult.status === "fulfilled") {
      drivers = driversResult.value.data.filter((driver) =>
        isOnlineDriverStatus(driver.status),
      );
      setDealerDrivers(drivers);
    }

    let coverageDetail: CoverageAreaOrderDetailDto | null = null;
    if (dealerId && coverageAreaId) {
      if (coverageResult.status === "rejected") {
        setCoverageAreaDetail(null);
      } else if (coverageResult.status === "fulfilled") {
        coverageDetail = coverageResult.value;
        setCoverageAreaDetail(coverageDetail);
      }
    }

    await fetchDriverInsights(coverageDetail, drivers);
    setLoadingDrivers(false);
  };

  const openSelection = () => {
    setLoadingDrivers(true);
    setIsSelecting(true);
    setSelectedDriver(null);
    loadSelectionData().catch(() => undefined);
  };

  const closeSelection = () => {
    setIsSelecting(false);
    setSelectedDriver(null);
    setDriverActiveOrdersById(undefined);
    setDriverInsightLoaded(false);
    setLoadingDrivers(false);
  };

  const applySelection = useCallback(async () => {
    if (!isSelecting || !selectedDriver) {
      return;
    }
    const isNoDriver = selectedDriver.id === NO_DRIVER_OPTION_ID;
    const payload = isNoDriver
      ? { dealerId, orderId, clearDriver: true }
      : {
          dealerId,
          orderId,
          driverName: selectedDriver.nameForAssignment,
          driverId: selectedDriver.id,
        };
    await dispatch(handleAddDriveOrderRequest(payload)).unwrap();
    setIsSelecting(false);
    setSelectedDriver(null);
    setDriverActiveOrdersById(undefined);
    setDriverInsightLoaded(false);
  }, [dealerId, orderId, selectedDriver, isSelecting, dispatch]);

  return {
    isSelecting,
    loadingDrivers,
    driverOptions,
    driverInsightLoaded,
    selectedDriver,
    setSelectedDriver,
    openSelection,
    closeSelection,
    applySelection,
  };
}
